import React from 'react';
import { TaskViewType } from '../types';
import TaskListView from './TaskListView';
import TaskMapView from './TaskMapView';
import ProjectFilterDropdown from 'component/ProjectFilterDropdown';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';

interface CurrentTasksCardProps {
  tasks: any[] | null;
  allTasksForMap: any[];
  loading: boolean;
  taskViewType: TaskViewType;
  contentHeight: string;
  search: string;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  selectedTask?: any;
  userId?: string;
  projectList?: any[];
  selectedProjectId?: string | null;
  onProjectChange?: (projectId: string | null) => void;
  mapLoading?: boolean;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (e: React.FormEvent) => void;
  onTaskViewTypeChange: (type: TaskViewType) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSelectTask: (task: any) => void;
}

const CurrentTasksCard: React.FC<CurrentTasksCardProps> = ({
  tasks,
  allTasksForMap,
  loading,
  taskViewType,
  contentHeight,
  search,
  currentPage,
  pageSize,
  totalRecords,
  totalPages,
  selectedTask,
  userId,
  projectList = [],
  selectedProjectId = null,
  onProjectChange,
  mapLoading = false,
  onSearchChange,
  onSearchSubmit,
  onTaskViewTypeChange,
  onPageChange,
  onPageSizeChange,
  onSelectTask,
}) => {
  const hasTasks = tasks && tasks.length > 0;
  const { navRef: taskViewNavRef, indicatorStyle: taskViewIndicatorStyle } = useNavPillsIndicator(taskViewType);

  return (
    <div className="card h-100 border" style={{ minHeight: '500px' }}>
      <div className="card-header bg-light border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-bold">Current Tasks</h6>
          <div className="d-flex align-items-center">
            {hasTasks && !loading && !mapLoading && (
              <span className="badge bg-secondary me-2">
                {taskViewType === 'map' ? allTasksForMap.length : totalRecords}
              </span>
            )}
            <div ref={taskViewNavRef} className="nav-pills-animated h-100">
              <div
                className="nav-pill-indicator"
                style={taskViewIndicatorStyle}
              />
              <button
                type="button"
                className={`nav-link btn ${taskViewType === "list" ? "active" : ""}`}
                onClick={() => onTaskViewTypeChange('list')}
                disabled={mapLoading}
              >
                <i className="fa-solid fa-list me-1"></i> List
              </button>
              <button
                type="button"
                className={`nav-link btn ${taskViewType === "map" ? "active" : ""}`}
                onClick={() => onTaskViewTypeChange('map')}
                disabled={mapLoading}
              >
                <i className="fa-solid fa-map me-1"></i> Map
                {mapLoading && (
                  <span className="spinner-border spinner-border-sm ms-1" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body p-0 d-flex flex-column" style={{
        flex: 1,
        height: `calc(${contentHeight} + ${hasTasks && taskViewType === 'list' && !mapLoading ? '70px' : '0px'})`
      }}>
        <div className="p-3 border-bottom">
          <form className="d-flex gap-2" onSubmit={onSearchSubmit}>
            <ProjectFilterDropdown
              projects={Array.isArray(projectList) ? projectList : []}
              defaultSelectedId={selectedProjectId}
              allowAllProject={true}
              onChange={(p) => onProjectChange && onProjectChange(p ? p.id : null)}
            />
            <input
              type="text"
              className="form-control form-control-compact"
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              disabled={!userId || mapLoading}
            />
            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={!userId || mapLoading}
            >
              <i className="fa-solid fa-search"></i>
            </button>
          </form>
        </div>

        <div className="flex-grow-1 position-relative">
          {taskViewType === 'list' ? (
            <TaskListView
              tasks={tasks}
              loading={loading}
              contentHeight={contentHeight}
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecords={totalRecords}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          ) : (
            mapLoading ? (
              <div className="text-center py-4" style={{ height: contentHeight }}>
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading map data...</span>
                </div>
                <p className="mt-2">Loading map data...</p>
              </div>
            ) : (
              <TaskMapView
                tasks={allTasksForMap}
                selectedTask={selectedTask}
                onSelectTask={onSelectTask}
                contentHeight={contentHeight}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CurrentTasksCard;